/* ============================================================
   CLOTHING PRODUCT PAGE — variant resolution

   Client-side, from a matrix the section emits. No section re-render, no
   fetch, no Section Rendering API round-trip, and none of Dawn's
   <product-info> / <variant-selects> machinery — Dawn replaces that whole
   element on every change via HTMLUpdateUtility.viewTransition, which
   cascaded into 67 section renders per click on the photograph page.

   Clothing differs from the photograph products in one way that matters
   more than any layout decision: THESE CAN SELL OUT. The Loxley
   never-sold-out invariant does NOT apply, so an unavailable combination
   must be distinguished from a non-existent one:

     * no variant for this combination  -> hidden (not a choice)
     * variant exists but unavailable   -> shown, struck through, disabled

   Collapsing those two into "hidden", as the photograph page correctly
   does, would silently hide a sold-out size here and tell the customer
   nothing.
   ============================================================ */

class MomVariantPicker extends HTMLElement {
  connectedCallback() {
    const script = this.querySelector('[data-mcv-matrix]');
    if (!script) return;

    try {
      this.data = JSON.parse(script.textContent);
    } catch (err) {
      console.error('[mom-product-variants] variant matrix is not valid JSON', err);
      return;
    }

    this.optionButtons = Array.from(this.querySelectorAll('[data-mcv-option]'));
    this.image = this.querySelector('[data-mpp-image]');
    this.priceNode = this.querySelector('[data-mcv-price]');
    this.variantInput = this.querySelector('.product-variant-id');
    this.submit = this.querySelector('[data-mcv-submit]');
    this.stock = this.querySelector('[data-mcv-stock]');
    this.wellbar = this.querySelector('[data-mcv-wellbar]');
    this.wallet = this.querySelector('.mpp__wallet');

    this.bind();
    this.selectInitial();
  }

  bind() {
    this.optionButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.dataset.exists === 'false') return; // no such combination
        this.state[btn.dataset.position] = btn.dataset.value;
        this.reconcileFrom(Number(btn.dataset.position));
        this.render();
      });
    });

    this.querySelectorAll('[data-mpp-qty]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const input = btn.closest('.mpp__qty').querySelector('input');
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(btn.dataset.mppQty));
      });
    });
  }

  /** Open on whatever Shopify resolved server-side, so ?variant= is honoured. */
  selectInitial() {
    const initial =
      this.data.variants.find((v) => v.id === this.data.selectedId) || this.data.variants[0];
    if (!initial) return;
    this.state = {};
    initial.options.forEach((value, i) => {
      this.state[i + 1] = value;
    });
    this.render();
  }

  matching(upTo) {
    return this.data.variants.filter((v) =>
      v.options.every((value, i) => i + 1 > upTo || this.state[i + 1] === value)
    );
  }

  /** Values that EXIST at `position` given everything chosen above it. */
  valuesAt(position) {
    const seen = [];
    this.matching(position - 1).forEach((v) => {
      const value = v.options[position - 1];
      if (value !== undefined && !seen.includes(value)) seen.push(value);
    });
    return seen;
  }

  /**
   * Keep the current value where it still exists, otherwise move to the
   * first that does — preferring one that is actually purchasable, so
   * changing colour does not land the customer on a sold-out size when a
   * available one is right there.
   */
  reconcileFrom(position) {
    const count = this.data.optionNames.length;
    for (let p = position + 1; p <= count; p += 1) {
      const valid = this.valuesAt(p);
      if (!valid.length) {
        delete this.state[p];
        continue;
      }
      if (valid.includes(this.state[p])) continue;

      const firstAvailable = valid.find((value) =>
        this.matching(p - 1).some(
          (v) => v.options[p - 1] === value && v.available !== false
        )
      );
      this.state[p] = firstAvailable || valid[0];
    }
  }

  currentVariant() {
    return (
      this.data.variants.find((v) => v.options.every((value, i) => this.state[i + 1] === value)) ||
      null
    );
  }

  render() {
    const variant = this.currentVariant();
    const count = this.data.optionNames.length;

    for (let p = 1; p <= count; p += 1) {
      const exists = this.valuesAt(p);

      this.optionButtons
        .filter((b) => Number(b.dataset.position) === p)
        .forEach((btn) => {
          const value = btn.dataset.value;
          const isPresent = exists.includes(value);

          // Does a purchasable variant exist for this value, given the
          // choices above it? Two different states, deliberately kept apart.
          const purchasable = this.matching(p - 1).some(
            (v) => v.options[p - 1] === value && v.available !== false
          );

          btn.hidden = !isPresent;
          btn.dataset.exists = isPresent ? 'true' : 'false';
          btn.dataset.soldOut = isPresent && !purchasable ? 'true' : 'false';
          btn.disabled = isPresent && !purchasable;
          btn.setAttribute('aria-pressed', this.state[p] === value ? 'true' : 'false');
        });
    }

    if (variant) {
      // priceFormatted comes from Liquid's `money` filter and can contain an
      // HTML entity for the currency symbol, so it is written as markup.
      if (this.priceNode) this.priceNode.innerHTML = variant.priceFormatted;

      const media = variant.mediaId && this.data.media[String(variant.mediaId)];
      if (media && this.image) {
        this.image.src = media.src;
        this.image.alt = media.alt;
      }

      if (this.variantInput) {
        this.variantInput.value = variant.id;
        this.variantInput.disabled = variant.available === false;
      }
    }

    const buyable = !!variant && variant.available !== false;

    if (this.submit) {
      this.submit.disabled = !buyable;
      const label = this.submit.querySelector('span');
      if (label) label.textContent = buyable ? this.data.addLabel : this.data.soldOutLabel;
    }

    // Shopify's accelerated checkout cannot buy an unavailable variant —
    // leaving it visible would offer a one-tap purchase that fails.
    if (this.wallet) this.wallet.hidden = !buyable;

    if (this.stock) {
      this.stock.classList.toggle('mpp__stock--out', !buyable);
      this.stock.innerHTML = buyable
        ? this.data.inStockHtml
        : `<strong>${this.data.soldOutLabel}</strong> — this combination is unavailable`;
    }

    if (this.wellbar) {
      this.wellbar.textContent = Object.keys(this.state)
        .sort()
        .map((k) => this.state[k])
        .join(' · ');
    }

    if (variant && window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }
}

if (!customElements.get('mom-product-variants')) {
  customElements.define('mom-product-variants', MomVariantPicker);
}
